/** @format */

import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { FaEdit, FaTimes, FaTrashAlt } from "react-icons/fa";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useDispatch } from "react-redux";
import countriesEn from "../../src/utils/data/countries_en.json";
import countriesFr from "../../src/utils/data/countries_fr.json";
import {
  add_customer_addresses,
  setDefaultAddress,
  updateAddressByIndex,
} from "../store/reducers/addressReducer";
import i18n from "../utils/i18n";

const AddressManager = ({ customerAddresses = [], onSelect, customerInfo }) => {
  // const { customerInfo} = useSelector(state => state.auth)
  // const { customerAddresses } = useSelector(state => state.address)

  const dispatch = useDispatch();

  //const countries = countryList().getData();

  const { t } = useTranslation();

  const countries = i18n.language === "fr" ? countriesFr : countriesEn;

  const [addresses, setAddresses] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formState, setFormState] = useState(initialFormState());
  const [showAddresses, setShowAddresses] = useState(false);

  const [editingIndex, setEditingIndex] = useState(null);

  const [formTitle, setFormTitle] = useState(null);

  const addressSource =
    customerAddresses.length > 0 ? customerAddresses : addresses;
  const hasDefault = addressSource.some(
    (addr) => addr?.default || addr.info?.default,
  );
  const selectedAddress =
    selectedIndex !== null
      ? addressSource[selectedIndex]
      : addressSource.find((a) => a?.default || a.info?.default);

  useEffect(() => {
    if (selectedIndex !== null && addressSource[selectedIndex]) {
      onSelect(addressSource[selectedIndex]);
    } else if (selectedAddress) {
      onSelect(selectedAddress);
    } else {
      onSelect(null);
    }
  }, [selectedIndex, addressSource, selectedAddress, onSelect]);

  function initialFormState() {
    return {
      country: "",
      countryCode: "tg", // ex: 'tg'
      name: "",
      phone: "",
      address: "",
      apartment: "",
      region: "",
      city: "",
      postalCode: "",
      default: false,
    };
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "country") {
      const selected = countries.find((c) => c.label === value);
      setFormState((prev) => ({
        ...prev,
        country: value,
        countryCode: selected?.value || "tg",
      }));
    } else {
      setFormState((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const required = [
      "country",
      "name",
      "phone",
      "address",
      "region",
      "city",
      "postalCode",
    ];
    const hasEmpty = required.some((key) => !formState[key]);
    if (hasEmpty) return toast.error(`${t("You must fill required fields")}`);

    if (editingIndex !== null && customerInfo?.id) {
      dispatch(
        updateAddressByIndex({
          customerId: customerInfo.id,
          index: editingIndex,
          updatedAddress: formState,
        }),
      );
      // toast.success("Adresse mise à jour !");
      setEditingIndex(null);
      setFormTitle(`${t("Edit the delivery address")}`);
      //setFormState(initialFormState());
    } else {
      // sinon, ajout comme avant
      const isFirst = addresses.length === 0 && customerAddresses.length === 0;
      const newAddress = { ...formState, default: isFirst };
      const updated = [...addresses, newAddress];
      setAddresses(updated);
      setSelectedIndex(updated.length - 1);
      setFormTitle(`${t("Add a delivery address")}`);
      if (customerInfo?.id) {
        dispatch(
          add_customer_addresses({
            info: newAddress,
            customerId: customerInfo.id,
          }),
        );
      }
    }
    setShowForm(false);
    setFormState(initialFormState());
  };

  /*
  "You must fill required fields":"Veuillez remplir tous les champs obligatoires.",
  "Edit the delivery address":"Modifier l'adresse de livraison",
  "Add a delivery address":"Ajouter une adresse de livraison",
  "Default address.":"Adresse par défaut définie.",
  "An error occured":"une erreur est survenue",
  "Add your delivery address":"Ajouter votre adresse de livraison",
  "Unknown address":"Unknown address",
  "default address":"default address",
  "None address set as default":"Aucune adresse n’est définie par défaut !",
  "Change":"Changer",
  "Select an address":"Select an address",
  "Set as default":"Set as default",
  "Add a new delivery address":"Ajouter une nouvelle addresse de livraison ",
  "Country":"Country",
  "Personal informations":"Informations personnelles",
  "LastName & Firstname":"LastName & Firstname",
  "Address":"Address",
  "Address (Road)":"Address (Road)",
  "Apartment / House (optional)":"Appartement / Maison (facultatif)",
  "District":"District",
  "City":"City"
  "Postal Code":"Postal Code",
  "Cancel":"Annuler",
  "Save":"Enregistrer"

  */

  const editAddress = (index) => {
    const current = addressSource[index];
    setFormState(current.info || current);
    setEditingIndex(index);
    setFormTitle(`${t("Edit the delivery address")}`);
    setShowForm(true);
  };

  const deleteAddress = (index) => {
    const updated = addressSource.filter((_, i) => i !== index);
    setAddresses(updated);
    if (selectedIndex === index) {
      setSelectedIndex(null);
    }
  };

  const setAsDefault = (index) => {
    const updated = addressSource.map((addr, i) => ({
      ...addr,
      default: i === index,
    }));
    setAddresses(updated);
    if (customerInfo?.id) {
      dispatch(setDefaultAddress({ index, customerId: customerInfo.id }))
        .unwrap()
        .then(() => {
          toast.success(`${t("Default address")}`);
        })
        .catch((err) => {
          toast.error(`${t("An error occured")}`);
          console.error(err);
        });
    }
  };

  useEffect(() => {
    if (addressSource.length === 0) {
      setFormTitle(`${t("Add your delivery address")}`);
    }
  }, [addressSource.length, t]);

  const AddressDisplay = ({ address }) => {
    if (!address) return <p>{t("Unknown address")}</p>;
    return (
      <div>
        <p>
          <strong>{address.name || address.info.name}</strong> - +
          {address.phone || address.info.phone}
        </p>
        <p>
          {address.address || address.info.address}{" "}
          {(address?.apartment || address.info?.apartment) &&
            `(${address?.apartment || address.info?.apartment})`}
        </p>
        <p>
          {address.city || address.info.city},{" "}
          {address.region || address.info.region},{" "}
          {address.country || address.info.country} (
          {address.postalCode || address.info.postalCode})
        </p>
        {(address?.default || address.info?.default) && (
          <span className='text-green-600 text-sm'>
            [{t("default address")}]
          </span>
        )}
      </div>
    );
  };

  return (
    <div className='space-y-4'>
      {addressSource.length > 0 && !hasDefault && (
        <div className='bg-yellow-100 text-yellow-800 px-4 py-2 rounded'>
          {t("None address set as default")}
        </div>
      )}

      {addressSource.length > 0 ? (
        <>
          {!showAddresses ? (
            selectedAddress && (
              <div className='flex items-start justify-between p-4 bg-white border rounded-md shadow'>
                <div className='flex-1'>
                  <label className='flex items-center space-x-2 cursor-pointer'>
                    <AddressDisplay address={selectedAddress} />
                  </label>
                </div>
                <button
                  onClick={() => setShowAddresses(true)}
                  className='text-indigo-500 mt-3'
                >
                  {t("Change")}
                </button>
              </div>
            )
          ) : (
            <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
              <div className='bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full relative'>
                <button
                  onClick={() => setShowAddresses(false)}
                  className='absolute top-3 right-3 text-gray-500 hover:text-black'
                >
                  <FaTimes />
                </button>
                <strong className='text-lg font-bold mb-3'>
                  {t("Select an address")}
                </strong>

                {addressSource.map((addr, index) => (
                  <div
                    key={index}
                    className='flex m-3 items-start justify-between p-4 bg-white border rounded-md shadow'
                  >
                    <div className='flex-1'>
                      <label className='flex items-center space-x-2 cursor-pointer'>
                        <input
                          type='radio'
                          name='selectedAddress'
                          checked={selectedIndex === index}
                          onChange={() => {
                            setSelectedIndex(index);
                            setShowAddresses(false);
                          }}
                          className='accent-blue-600'
                        />
                        <AddressDisplay address={addr} />
                      </label>
                    </div>
                    <div className='flex flex-col items-end space-y-1 text-sm'>
                      <button
                        onClick={() => editAddress(index)}
                        className='text-yellow-500'
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => deleteAddress(index)}
                        className='text-red-500'
                      >
                        <FaTrashAlt />
                      </button>
                      {!addr.default && (
                        <button
                          onClick={() => setAsDefault(index)}
                          className='text-green-500'
                        >
                          {t("Set as default")}
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                <button
                  onClick={() => {
                    setFormTitle(`${t("Add a delivery address")}`);
                    setFormState(initialFormState());
                    setEditingIndex(null);
                    setShowForm(true);
                  }}
                  className='text-white m-3 p-1 bg-red-600 w-full rounded-full shadow'
                >
                  {t("Add a new delivery address")}
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className='items-center justify-center'>
          <div className='bg-white p-6 rounded-lg shadow w-full relative'>
            <h3 className='text-xl font-semibold mb-4'>
              {formTitle || `${t("Add a delivery address")}`}
            </h3>
            {renderForm()}
          </div>
        </div>
      )}

      {showForm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full relative'>
            <button
              onClick={() => setShowForm(false)}
              className='absolute top-3 right-3 text-gray-500 hover:text-black'
            >
              <FaTimes />
            </button>
            <h3 className='text-xl font-semibold mb-4'>
              {formTitle || `${t("Add a delivery address")}`}
            </h3>
            {renderForm()}
          </div>
        </div>
      )}
    </div>
  );
  function renderForm() {
    return (
      <form onSubmit={handleSubmit} className='space-y-8 gap-5'>
        <div className='flex flex-col gap-2 mb-2'>
          <label htmlFor='' className='text-xl font-semibold mb-1'>
            {t("Country")}
          </label>
          <select
            name='country'
            value={formState.country}
            onChange={handleChange}
            className='w-full p-2 border border-gray-300 rounded'
          >
            <option value=''>
              {i18n.language === "fr" ? "Choisir un pays" : "Select a country"}
            </option>
            {countries.map((c) => (
              <option key={c.value} value={c.label}>
                {c.label}{" "}
              </option>
            ))}
          </select>
        </div>

        <div className='flex flex-col gap-2 mb-2'>
          <label htmlFor='' className='text-xl font-semibold mb-1'>
            {t("Personal informations")}
          </label>
          <div className='grid grid-cols-2 gap-4'>
            <input
              name='name'
              placeholder={t("LastName & Firstname")}
              value={formState.name}
              onChange={handleChange}
              className='px-2 border border-gray-300 rounded'
            />

            <PhoneInput
              country={formState.countryCode || "tg"}
              value={formState.phone}
              onChange={(phone) => setFormState((prev) => ({ ...prev, phone }))}
              inputStyle={{
                width: "90%",
                padding: "0.5rem",
                border: "1px solid #e2e8f0",
                borderRadius: "0.375rem",
                left: "10%",
                right: "10%",
              }}
              enableSearch={true}
            />
          </div>
        </div>

        <div className='flex flex-col gap-2 mb-2'>
          <label htmlFor='' className='text-xl font-semibold mb-1'>
            {t("Address")}
          </label>
          <div className='flex flex-col gap-2 mb-2'>
            <div className='grid grid-cols-2 gap-4'>
              <input
                name='address'
                placeholder={t("Address (Road)")}
                value={formState.address}
                onChange={handleChange}
                className='p-2 border border-gray-300 rounded'
              />
              <input
                name='apartment'
                placeholder={t("Apartment / House (optional)")}
                value={formState.apartment}
                onChange={handleChange}
                className='p-2 border border-gray-300 rounded'
              />
            </div>

            <div className='grid grid-cols-3 gap-4'>
              <input
                name='region'
                placeholder={t("District")}
                value={formState.region}
                onChange={handleChange}
                className='p-2 border border-gray-300 rounded'
              />
              <input
                name='city'
                placeholder={t("City")}
                value={formState.city}
                onChange={handleChange}
                className='p-2 border border-gray-300 rounded'
              />
              <input
                name='postalCode'
                placeholder={t("Postal Code")}
                value={formState.postalCode}
                onChange={handleChange}
                className='p-2 border border-gray-300 rounded'
              />
            </div>
          </div>
        </div>

        <div className='flex justify-end space-x-4'>
          <button
            type='button'
            onClick={() => {
              setShowForm(false);
              setFormState(initialFormState());
            }}
            className='bg-gray-300 text-gray-700 px-4 py-2 rounded'
          >
            {t("Cancel")}
          </button>
          <button
            type='submit'
            className='bg-blue-600 text-white px-4 py-2 rounded'
          >
            {t("Save")}
          </button>
        </div>
      </form>
    );
  }
};

export default AddressManager;
